/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable no-mixed-operators */
import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  // Radio, RadioGroup, FormControlLabel, IconButton - unused but may be needed later
  TextField,
  Button,
  Box,
  Grid,
  Chip,
  Alert,
  Divider,
  InputAdornment,
  CircularProgress,
} from '@mui/material';
import {
  CreditCard,
  AccountBalance,
  PhoneAndroid,
  QrCode,
  Security,
  LocalAtm,
  ShoppingCart,
} from '@mui/icons-material';
import { apiService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

interface PaymentMethod {
  id: string;
  name: string;
  type: 'upi' | 'card' | 'wallet' | 'bnpl' | 'cod';
  icon: React.ReactNode;
  description: string;
  available: boolean;
  processingFee?: number;
  cashback?: string;
  color?: string;
  isDefault?: boolean;
}

interface ApiPaymentMethod {
  id: string;
  user_id: string;
  type: string;
  name: string;
  details: any;
  is_default: boolean;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

interface PaymentMethodSelectorProps {
  selectedMethod: string;
  onMethodSelect: (methodId: string) => void;
  onPaymentDetails: (details: any) => void;
  totalAmount: number;
}

const PaymentMethodSelector: React.FC<PaymentMethodSelectorProps> = ({
  selectedMethod,
  onMethodSelect,
  onPaymentDetails,
  totalAmount,
}) => {
  const { user, token } = useAuth();
  const [paymentDetails, setPaymentDetails] = useState<any>({});
  const [showDetails, setShowDetails] = useState(false);
  const [errors, setErrors] = useState<any>({});
  const [loading, setLoading] = useState(false);
  const [userPaymentMethods, setUserPaymentMethods] = useState<ApiPaymentMethod[]>([]);
  const [availablePaymentMethods, setAvailablePaymentMethods] = useState<PaymentMethod[]>([]);

  // Load payment methods on component mount
  useEffect(() => {
    loadPaymentMethods();
  }, [token]);

  const loadPaymentMethods = async () => {
    setLoading(true);
    try {
      // Load available payment methods (public endpoint)
      const availableMethods = await apiService.getPaymentMethods();
      setAvailablePaymentMethods(availableMethods);

      // Load user's saved payment methods if authenticated
      if (token) {
        const userMethods = await apiService.getPaymentMethods(token);
        setUserPaymentMethods(userMethods);
      }
    } catch (error) {
      console.error('Error loading payment methods:', error);
      // Fallback to default methods
      setAvailablePaymentMethods(getDefaultPaymentMethods());
    } finally {
      setLoading(false);
    }
  };

  const getDefaultPaymentMethods = (): PaymentMethod[] => [
    {
      id: 'upi',
      name: 'UPI',
      type: 'upi',
      icon: <PhoneAndroid />,
      description: 'Pay using UPI ID or QR Code',
      available: true,
      cashback: '2% cashback',
      color: '#667eea',
    },
    {
      id: 'credit_card',
      name: 'Credit Card',
      type: 'card',
      icon: <CreditCard />,
      description: 'Visa, Mastercard, RuPay',
      available: true,
      processingFee: 2.5,
      color: '#e74c3c',
    },
    {
      id: 'debit_card',
      name: 'Debit Card',
      type: 'card',
      icon: <CreditCard />,
      description: 'Visa, Mastercard, RuPay',
      available: true,
      processingFee: 1.5,
      color: '#3498db',
    },
    {
      id: 'netbanking',
      name: 'Net Banking',
      type: 'card',
      icon: <AccountBalance />,
      description: 'All major banks supported',
      available: true,
      processingFee: 2.0,
      color: '#2ecc71',
    },
    {
      id: 'google_pay',
      name: 'Google Pay',
      type: 'wallet',
      icon: <PhoneAndroid />,
      description: 'Quick and secure payments',
      available: true,
      cashback: '1% cashback',
      color: '#4285f4',
    },
    {
      id: 'phonepe',
      name: 'PhonePe',
      type: 'wallet',
      icon: <PhoneAndroid />,
      description: 'Instant payments',
      available: true,
      cashback: '1% cashback',
      color: '#5f259f',
    },
    {
      id: 'paytm',
      name: 'Paytm',
      type: 'wallet',
      icon: <LocalAtm />,
      description: 'Paytm wallet & UPI',
      available: true,
      cashback: '1% cashback',
      color: '#00baf2',
    },
    {
      id: 'apple_pay',
      name: 'Apple Pay',
      type: 'wallet',
      icon: <PhoneAndroid />,
      description: 'Touch ID / Face ID',
      available: true,
      color: '#000000',
    },
    {
      id: 'klarna',
      name: 'Klarna',
      type: 'bnpl',
      icon: <ShoppingCart />,
      description: 'Pay in 4 interest-free installments',
      available: true,
      color: '#ffb3c7',
    },
    {
      id: 'afterpay',
      name: 'Afterpay',
      type: 'bnpl',
      icon: <ShoppingCart />,
      description: 'Buy now, pay later',
      available: true,
      color: '#b2fce4',
    },
    {
      id: 'cod',
      name: 'Cash on Delivery',
      type: 'cod',
      icon: <LocalAtm />,
      description: 'Pay when you receive',
      available: true,
      processingFee: 50,
      color: '#f39c12',
    },
  ];

  // Get the payment methods to display (user's saved methods + available methods)
  const getDisplayPaymentMethods = (): PaymentMethod[] => {
    const methods = [...availablePaymentMethods];
    
    // Add user's saved payment methods if authenticated
    if (userPaymentMethods.length > 0) {
      userPaymentMethods.forEach(userMethod => {
        // Convert API response to component format
        const displayMethod: PaymentMethod = {
          id: userMethod.id,
          name: userMethod.name,
          type: userMethod.type as 'upi' | 'card' | 'wallet' | 'bnpl' | 'cod',
          icon: getIconForType(userMethod.type),
          description: userMethod.details?.description || `${userMethod.type} payment method`,
          available: userMethod.is_active,
          color: getColorForType(userMethod.type),
          isDefault: userMethod.is_default,
        };
        methods.unshift(displayMethod); // Add user methods at the top
      });
    }
    
    return methods;
  };

  const getIconForType = (type: string) => {
    switch (type) {
      case 'upi': return <PhoneAndroid />;
      case 'card': return <CreditCard />;
      case 'wallet': return <PhoneAndroid />;
      case 'bnpl': return <ShoppingCart />;
      case 'cod': return <LocalAtm />;
      default: return <CreditCard />;
    }
  };

  const getColorForType = (type: string) => {
    switch (type) {
      case 'upi': return '#667eea';
      case 'card': return '#e74c3c';
      case 'wallet': return '#4285f4';
      case 'bnpl': return '#ffb3c7';
      case 'cod': return '#f39c12';
      default: return '#666';
    }
  };

  const handleMethodSelect = (methodId: string) => {
    onMethodSelect(methodId);
    setShowDetails(true);
    setPaymentDetails({});
    setErrors({});
  };

  const validateForm = () => {
    const newErrors: any = {};
    const methods = getDisplayPaymentMethods();
    const method = methods.find(m => m.id === selectedMethod);
    
    if (!method) return false;

    if (method.type === 'upi' && !paymentDetails.upiId) {
      newErrors.upiId = 'UPI ID is required';
    }

    if (method.type === 'card') {
      if (!paymentDetails.cardNumber) newErrors.cardNumber = 'Card number is required';
      if (!paymentDetails.expiry) newErrors.expiry = 'Expiry date is required';
      if (!paymentDetails.cvv) newErrors.cvv = 'CVV is required';
      if (!paymentDetails.cardholderName) newErrors.cardholderName = 'Cardholder name is required';
    }

    if (method.type === 'wallet' && !paymentDetails.walletId) {
      newErrors.walletId = 'Wallet ID is required';
    }

    if (method.type === 'bnpl' && !paymentDetails.phone) {
      newErrors.phone = 'Phone number is required for verification';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handlePaymentDetails = () => {
    if (!selectedMethod) {
      alert('Please select a payment method');
      return;
    }

    if (!validateForm()) {
      return;
    }

    const methods = getDisplayPaymentMethods();
    const method = methods.find(m => m.id === selectedMethod);
    if (!method) return;

    onPaymentDetails({
      method: method,
      details: paymentDetails,
      totalAmount: totalAmount + (method.processingFee || 0),
    });
  };

  const formatCardNumber = (value: string) => {
    const v = value.replace(/\s+/g, '').replace(/[^0-9]/gi, '');
    const matches = v.match(/\d{4,16}/g);
    const match = matches && matches[0] || '';
    const parts = [];
    for (let i = 0, len = match.length; i < len; i += 4) {
      parts.push(match.substring(i, i + 4));
    }
    if (parts.length) {
      return parts.join(' ');
    } else {
      return v;
    }
  };

  const formatExpiry = (value: string) => {
    const v = value.replace(/\s+/g, '').replace(/[^0-9]/gi, '');
    if (v.length >= 2) {
      return v.substring(0, 2) + '/' + v.substring(2, 4);
    }
    return v;
  };

  const renderUPIForm = () => (
    <Box>
      <Typography variant="h6" gutterBottom>
        UPI Payment Details
      </Typography>
      <TextField
        fullWidth
        label="UPI ID"
        placeholder="Enter UPI ID (e.g., user@paytm)"
        value={paymentDetails.upiId || ''}
        onChange={(e) => setPaymentDetails({ ...paymentDetails, upiId: e.target.value })}
        error={!!errors.upiId}
        helperText={errors.upiId}
        margin="normal"
        InputProps={{
          startAdornment: <InputAdornment position="start">📱</InputAdornment>,
        }}
      />
      <Button
        variant="outlined"
        startIcon={<QrCode />}
        fullWidth
        sx={{ mt: 2 }}
      >
        Scan QR Code
      </Button>
    </Box>
  );

  const renderCardForm = () => (
    <Box>
      <Typography variant="h6" gutterBottom>
        Card Payment Details
      </Typography>
      <TextField
        fullWidth
        label="Card Number"
        placeholder="1234 5678 9012 3456"
        value={paymentDetails.cardNumber || ''}
        onChange={(e) => setPaymentDetails({ 
          ...paymentDetails, 
          cardNumber: formatCardNumber(e.target.value) 
        })}
        error={!!errors.cardNumber}
        helperText={errors.cardNumber}
        margin="normal"
        InputProps={{
          startAdornment: <InputAdornment position="start"><CreditCard /></InputAdornment>,
        }}
      />
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <TextField
            fullWidth
            label="Expiry Date"
            placeholder="MM/YY"
            value={paymentDetails.expiry || ''}
            onChange={(e) => setPaymentDetails({ 
              ...paymentDetails, 
              expiry: formatExpiry(e.target.value) 
            })}
            error={!!errors.expiry}
            helperText={errors.expiry}
            margin="normal"
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            fullWidth
            label="CVV"
            placeholder="123"
            value={paymentDetails.cvv || ''}
            onChange={(e) => setPaymentDetails({ ...paymentDetails, cvv: e.target.value })}
            error={!!errors.cvv}
            helperText={errors.cvv}
            margin="normal"
            type="password"
            InputProps={{
              endAdornment: <InputAdornment position="end"><Security /></InputAdornment>,
            }}
          />
        </Grid>
      </Grid>
      <TextField
        fullWidth
        label="Cardholder Name"
        value={paymentDetails.cardholderName || ''}
        onChange={(e) => setPaymentDetails({ ...paymentDetails, cardholderName: e.target.value })}
        error={!!errors.cardholderName}
        helperText={errors.cardholderName}
        margin="normal"
      />
    </Box>
  );

  const renderWalletForm = () => (
    <Box>
      <Typography variant="h6" gutterBottom>
        Wallet Payment Details
      </Typography>
      <TextField
        fullWidth
        label="Wallet ID or Phone Number"
        value={paymentDetails.walletId || ''}
        onChange={(e) => setPaymentDetails({ ...paymentDetails, walletId: e.target.value })}
        error={!!errors.walletId}
        helperText={errors.walletId}
        margin="normal"
        InputProps={{
          startAdornment: <InputAdornment position="start">📱</InputAdornment>,
        }}
      />
      <Button
        variant="contained"
        color="success"
        fullWidth
        sx={{ mt: 2 }}
        startIcon={<PhoneAndroid />}
      >
        Quick Pay
      </Button>
    </Box>
  );

  const renderBNPLForm = () => (
    <Box>
      <Typography variant="h6" gutterBottom>
        Buy Now, Pay Later
      </Typography>
      <Alert severity="info" sx={{ mb: 2 }}>
        <Typography variant="body2">
          • Pay in 4 interest-free installments<br/>
          • First payment: ₹{(totalAmount / 4).toFixed(2)}<br/>
          • Remaining 3 payments: ₹{(totalAmount / 4).toFixed(2)} each
        </Typography>
      </Alert>
      <TextField
        fullWidth
        label="Phone Number"
        placeholder="Enter phone number for verification"
        value={paymentDetails.phone || ''}
        onChange={(e) => setPaymentDetails({ ...paymentDetails, phone: e.target.value })}
        error={!!errors.phone}
        helperText={errors.phone}
        margin="normal"
        InputProps={{
          startAdornment: <InputAdornment position="start">📞</InputAdornment>,
        }}
      />
    </Box>
  );

  const renderCODForm = () => (
    <Box>
      <Typography variant="h6" gutterBottom>
        Cash on Delivery
      </Typography>
      <Alert severity="warning" sx={{ mb: 2 }}>
        <Typography variant="body2">
          • Pay ₹{totalAmount + 50} when you receive your order<br/>
          • Cash handling fee: ₹50<br/>
          • Keep exact change ready
        </Typography>
      </Alert>
    </Box>
  );

  const renderPaymentForm = () => {
    const methods = getDisplayPaymentMethods();
    const method = methods.find(m => m.id === selectedMethod);
    if (!method) return null;

    switch (method.type) {
      case 'upi':
        return renderUPIForm();
      case 'card':
        return renderCardForm();
      case 'wallet':
        return renderWalletForm();
      case 'bnpl':
        return renderBNPLForm();
      case 'cod':
        return renderCODForm();
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
        <Typography variant="body1" sx={{ ml: 2 }}>
          Loading payment methods...
        </Typography>
      </Box>
    );
  }

  const displayMethods = getDisplayPaymentMethods();

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Select Payment Method
      </Typography>
      
      {userPaymentMethods.length > 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="body2">
            Your saved payment methods are shown at the top. You can also choose from available payment options below.
          </Typography>
        </Alert>
      )}
      
      <Grid container spacing={2}>
        {displayMethods.map((method) => (
          <Grid item xs={12} sm={6} md={4} key={method.id}>
            <Card
              sx={{
                cursor: method.available ? 'pointer' : 'not-allowed',
                opacity: method.available ? 1 : 0.5,
                border: selectedMethod === method.id ? 2 : 1,
                borderColor: selectedMethod === method.id ? method.color : 'divider',
                '&:hover': method.available ? {
                  boxShadow: 4,
                  transform: 'translateY(-2px)',
                } : {},
                transition: 'all 0.2s ease-in-out',
              }}
              onClick={() => method.available && handleMethodSelect(method.id)}
            >
              <CardContent>
                <Box display="flex" alignItems="center" mb={1}>
                  <Box
                    sx={{
                      color: method.color,
                      mr: 2,
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    {method.icon}
                  </Box>
                  <Typography variant="h6" component="div">
                    {method.name}
                  </Typography>
                  <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
                    {method.isDefault && (
                      <Chip
                        label="Default"
                        color="success"
                        size="small"
                        variant="outlined"
                      />
                    )}
                    {selectedMethod === method.id && (
                      <Chip
                        label="Selected"
                        color="primary"
                        size="small"
                      />
                    )}
                  </Box>
                </Box>
                <Typography variant="body2" color="text.secondary" mb={1}>
                  {method.description}
                </Typography>
                <Box display="flex" gap={1} flexWrap="wrap">
                  {method.cashback && (
                    <Chip
                      label={method.cashback}
                      color="success"
                      size="small"
                      variant="outlined"
                    />
                  )}
                  {method.processingFee && (
                    <Chip
                      label={`Fee: ₹${method.processingFee}`}
                      color="error"
                      size="small"
                      variant="outlined"
                    />
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {showDetails && selectedMethod && (
        <Card sx={{ mt: 3 }}>
          <CardContent>
            {renderPaymentForm()}
            <Divider sx={{ my: 2 }} />
            <Button
              variant="contained"
              size="large"
              fullWidth
              onClick={handlePaymentDetails}
              sx={{
                background: 'linear-gradient(45deg, #667eea 30%, #764ba2 90%)',
                '&:hover': {
                  background: 'linear-gradient(45deg, #5a6fd8 30%, #6a4190 90%)',
                },
              }}
            >
              Confirm Payment Method
            </Button>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default PaymentMethodSelector;
